export type User = {
  id: number;
  email: string;
  is_staff: boolean;
  is_superuser: boolean;
  is_active: boolean;
  is_verified: boolean;
  first_name?: string;
  last_name?: string;
  created_at?: string;
};

export type Session = {
  access: string;
  refresh: string;
  user_id: number;
  email: string;
  is_staff?: boolean;
};

export type Team = { id: number; name: string; league: number; league_id?: number; league_name?: string; image?: string | null };
export type League = {
  id: number;
  name: string;
  image: string | null;
  is_active: boolean;
  first_place_points: number;
  second_place_points: number;
  third_place_points: number;
  fourth_place_points: number;
  fifth_place_points: number;
  sixth_place_points: number;
  teams: Team[];
};
export type Prediction = {
  id: number;
  league: number;
  league_name: string;
  predicted_team: number;
  predicted_team_name: string;
  points: number;
  is_predicted: boolean;
  created_at: string;
  updated_at: string;
};
export type Profile = {
  id: number;
  email: string;
  image: string | null;
  description: string;
  first_name: string;
  last_name: string;
  created_date: string;
};
export type LeaderboardRow = {
  id: number;
  user__email?: string;
  profile__user__email?: string;
  first_name: string;
  last_name: string;
  total_points?: number;
  points?: number;
  image: string | null;
  rank: number;
};
export type AdminLeague = League & { teams: Team[] };
export type AdminUser = User;
export type AdminResult = {
  id: number;
  league_id: number;
  league_name: string;
  first_place_id: number;
  second_place_id: number;
  third_place_id: number;
  fourth_place_id: number;
  fifth_place_id: number;
  sixth_place_id: number;
  updated_at: string;
};

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:8787").replace(/\/$/, "");
const SESSION_KEY = "elmosliga_session";

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) as Session : null;
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

async function parseResponse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

async function request<T>(path: string, options: RequestInit = {}, allowRefresh = true): Promise<T> {
  const headers = new Headers(options.headers);
  const isFormData = options.body instanceof FormData;
  if (!isFormData && options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const session = getSession();
  if (session?.access) headers.set("Authorization", `Bearer ${session.access}`);
  const response = await fetch(`${API_URL}${path}`, { ...options, headers });
  const body = await parseResponse(response) as { error?: string; access?: string; refresh?: string } | T | null;
  if (response.status === 401 && allowRefresh && session?.refresh && !path.includes("/jwt/refresh")) {
    try {
      const refreshed = await request<{ access: string; refresh?: string }>("/accounts/api/v1/jwt/refresh", { method: "POST", body: JSON.stringify({ refresh: session.refresh }) }, false);
      saveSession({ ...session, access: refreshed.access, refresh: refreshed.refresh || session.refresh });
      return request<T>(path, options, false);
    } catch {
      clearSession();
    }
  }
  if (!response.ok) throw new Error((body as { error?: string } | null)?.error || `Request failed (${response.status})`);
  return body as T;
}

const json = (value: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(value) });

export const api = {
  register: (email: string, password: string, password1: string) => request<{ message: string }>("/accounts/api/v1/register", json({ email, password, password1 })),
  login: (email: string, password: string) => request<Session>("/accounts/api/v1/jwt/create", json({ email, password })),
  logout: () => request<{ message: string }>("/accounts/api/v1/token/logout", { method: "POST" }),
  activate: (token: string) => request<{ message: string }>(`/accounts/api/v1/activate/${encodeURIComponent(token)}`),
  resendActivation: (email: string) => request<{ message: string }>("/accounts/api/v1/resend-activation", json({ email })),
  passwordReset: (email: string) => request<{ message: string }>("/accounts/api/v1/password-reset", json({ email })),
  passwordResetConfirm: (token: string, new_password: string, new_password1: string) => request<{ message: string }>(`/accounts/api/v1/password-reset/confirm/${encodeURIComponent(token)}`, json({ new_password, new_password1 })),
  changePassword: (value: Record<string, string>) => request<{ details: string }>("/accounts/api/v1/password-change", { method: "PUT", body: JSON.stringify(value) }),
  profile: () => request<Profile>("/accounts/api/v1/profile"),
  updateProfile: (value: FormData | Record<string, string>) => request<Profile>("/accounts/api/v1/profile", { method: "PATCH", body: value instanceof FormData ? value : JSON.stringify(value) }),
  leagues: () => request<League[]>("/league/leagues"),
  teams: (leagueId: number) => request<Team[]>(`/league/leagues/${leagueId}/teams`),
  predictions: () => request<Prediction[]>("/league/predictions"),
  predictionStatus: (leagueId: number) => request<{ has_predicted: boolean; prediction?: Prediction }>(`/league/predictions/check/${leagueId}`),
  savePrediction: (league: number, predicted_team: number) => request<{ message: string; prediction: Prediction }>("/league/prediction", json({ league, predicted_team })),
  leaderboard: (leagueId?: number) => request<LeaderboardRow[]>(leagueId ? `/league/leaderboard/${leagueId}` : "/league/leaderboard"),
  adminSummary: () => request<{ users: number; leagues: number; teams: number; predictions: number }>("/admin/api/summary"),
  adminUsers: () => request<AdminUser[]>("/admin/api/users"),
  updateUser: (id: number, value: Partial<AdminUser>) => request<{ message: string }>(`/admin/api/users/${id}`, { method: "PATCH", body: JSON.stringify(value) }),
  adminLeagues: () => request<AdminLeague[]>("/admin/api/leagues"),
  createLeague: (value: Record<string, unknown>) => request<{ id: number; message: string }>("/admin/api/leagues", json(value)),
  updateLeague: (id: number, value: Record<string, unknown>) => request<{ message: string }>(`/admin/api/leagues/${id}`, { method: "PATCH", body: JSON.stringify(value) }),
  deleteLeague: (id: number) => request<{ message: string }>(`/admin/api/leagues/${id}`, { method: "DELETE" }),
  adminTeams: () => request<Team[]>("/admin/api/teams"),
  createTeam: (value: Record<string, unknown>) => request<{ id: number; message: string }>("/admin/api/teams", json(value)),
  updateTeam: (id: number, value: Record<string, unknown>) => request<{ message: string }>(`/admin/api/teams/${id}`, { method: "PATCH", body: JSON.stringify(value) }),
  deleteTeam: (id: number) => request<{ message: string }>(`/admin/api/teams/${id}`, { method: "DELETE" }),
  adminResults: () => request<AdminResult[]>("/league/admin/results"),
  saveResult: (value: Record<string, unknown>) => request<{ message: string }>("/league/admin/result/create", json(value)),
};

export function apiBaseUrl(): string {
  return API_URL;
}
